import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  EmptyState,
  IconButton,
} from '../components/ui';
import { ArrowPathIcon, MagnifyingGlassIcon, EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface Buyer {
  id: number;
  login: string;
  requisites_id: number | null;
  created_at: string;
}

interface BuyerDetails {
  buyer: Buyer;
  statistics: {
    total_deposits: Record<string, number>;
    total_withdrawals: Record<string, number>;
    net_balance: Record<string, number>;
  };
  senders: Array<{
    sender_name: string;
    sender_account: string | null;
    transaction_count: number;
    total_amount: number;
    currency: string;
    first_transaction_at: string;
    last_transaction_at: string;
  }>;
}

interface BuyersResponse {
  buyers: Buyer[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

const currencySymbols: Record<string, string> = {
  RUB: '₽',
  KZT: '₸',
  USD: '$',
  EUR: '€',
};

const formatCurrency = (amount: number, currency: string): string => {
  return `${amount.toLocaleString('ru-RU')} ${currencySymbols[currency] || currency}`;
};

interface ExportJob {
  id: string;
  kind: 'buyers';
  status: 'pending' | 'running' | 'done' | 'failed';
  filename?: string;
  error?: string;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
}

const Buyers: React.FC = () => {
  const { user } = useAuth();
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerDetails | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [downloading, setDownloading] = useState<'buyers' | null>(null);
  const pageSize = 50;

  const shopId = user?.shopId;

  // Получаем список покупателей
  const buyersQuery = useQuery<BuyersResponse>({
    queryKey: ['buyers', shopId, page, pageSize],
    queryFn: async () => {
      if (!shopId) throw new Error('Shop ID is required');
      const { data } = await api.get(
        `/buyers-analytics/buyers?shopId=${shopId}&limit=${pageSize}&offset=${(page - 1) * pageSize}`
      );
      return data;
    },
    enabled: !!shopId && (user?.role === 'admin' || user?.role === 'operator'),
    refetchInterval: 30000, // Обновляем каждые 30 секунд
  });

  // Получаем детальную информацию о покупателе
  const buyerDetailsQuery = useQuery<BuyerDetails>({
    queryKey: ['buyer-details', selectedBuyer?.buyer.id, shopId],
    queryFn: async () => {
      if (!shopId || !selectedBuyer?.buyer.id) throw new Error('Shop ID and Buyer ID are required');
      const { data } = await api.get(
        `/buyers-analytics/buyers/${selectedBuyer.buyer.id}?shopId=${shopId}`
      );
      return data;
    },
    enabled: !!shopId && !!selectedBuyer?.buyer.id,
  });

  const buyers = buyersQuery.data?.buyers ?? [];
  const isLoading = buyersQuery.isLoading;
  const error = buyersQuery.error;

  // Фильтрация по поиску
  const filteredBuyers = buyers.filter((buyer) => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      buyer.login.toLowerCase().includes(searchLower) ||
      String(buyer.id).includes(searchLower) ||
      (buyer.requisites_id && String(buyer.requisites_id).includes(searchLower))
    );
  });

  const handleViewDetails = async (buyer: Buyer) => {
    // Сначала устанавливаем базовую информацию о покупателе
    setSelectedBuyer({
      buyer,
      statistics: {
        total_deposits: {},
        total_withdrawals: {},
        net_balance: {},
      },
      senders: [],
    });
  };

  // Обновляем детали при загрузке
  useEffect(() => {
    if (buyerDetailsQuery.data && selectedBuyer) {
      setSelectedBuyer(buyerDetailsQuery.data);
    }
  }, [buyerDetailsQuery.data, selectedBuyer]);

  const startExport = async () => {
    try {
      setDownloading('buyers');
      setExportJob({ id: 'creating', kind: 'buyers', status: 'pending' });
      const create = await api.post('/admin/exports/jobs', {
        kind: 'buyers',
      });
      
      const jobId = create.data?.jobId as string;
      if (!jobId) {
        throw new Error('Job ID not received from server');
      }
      setExportJob({ id: jobId, kind: 'buyers', status: 'running' });

      const poll = async () => {
        const statusResp = await api.get(`/admin/exports/jobs/${jobId}`);
        const s = statusResp.data as any;
        setExportJob({
          id: jobId,
          kind: 'buyers',
          status: s.status,
          filename: s.filename,
          error: s.error,
          createdAt: s.createdAt,
          startedAt: s.startedAt,
          finishedAt: s.finishedAt,
        });
        if (s.status === 'done' || s.status === 'failed') return;
        setTimeout(poll, 1200);
      };
      poll();
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error || e?.message || 'Не удалось создать задачу экспорта';
      setExportJob({ id: 'error', kind: 'buyers', status: 'failed', error: errorMessage });
      alert(errorMessage);
    } finally {
      setDownloading(null);
    }
  };

  const downloadReady = async () => {
    if (!exportJob || exportJob.status !== 'done') return;
    try {
      setDownloading('buyers');
      const response = await api.get(`/admin/exports/jobs/${exportJob.id}/download`, { responseType: 'blob' });
      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const filename = exportJob.filename || `buyers_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Не удалось скачать файл');
    } finally {
      setDownloading(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (start: string, end?: string): string => {
    if (!start || !end) return '—';
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    const duration = Math.round((endTime - startTime) / 1000);
    if (duration < 60) return `${duration} сек`;
    return `${Math.floor(duration / 60)} мин ${duration % 60} сек`;
  };

  const getEstimatedSize = (): string => {
    const total = buyersQuery.data?.meta.total || 0;
    // Примерно 200 байт на строку покупателя
    const estimatedBytes = total * 200;
    return formatFileSize(estimatedBytes);
  };

  if (user?.role !== 'admin' && user?.role !== 'operator') {
    return <div className="text-center text-red-600 p-8">Доступ запрещен</div>;
  }

  if (!shopId) {
    return <div className="text-center text-red-600 p-8">Shop ID не найден</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Покупатели</h1>
          <p className="text-sm text-slate-500">Просмотр всех покупателей и их статистики</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={startExport}
            disabled={downloading !== null || (exportJob?.status === 'running' || exportJob?.status === 'pending')}
            className="flex items-center gap-2 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            {downloading === 'buyers' ? 'Готовим...' : 'Экспорт в Excel'}
          </button>
          <IconButton
            onClick={() => buyersQuery.refetch()}
            aria-label="Обновить список"
            variant="ghost"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </IconButton>
        </div>
      </div>

      {/* Прогресс экспорта */}
      {exportJob && (
        <Card className="border-primary-200 bg-primary-50">
          <CardBody className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="text-sm font-semibold text-[var(--app-heading)]">
                  Экспорт покупателей в Excel
                </div>
                <div className="text-xs text-[var(--app-text-muted)]">
                  {exportJob.status === 'pending' && 'Ожидание запуска...'}
                  {exportJob.status === 'running' && 'Формирование файла...'}
                  {exportJob.status === 'done' && 'Файл готов к скачиванию'}
                  {exportJob.status === 'failed' && `Ошибка: ${exportJob.error || 'Неизвестная ошибка'}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {exportJob.status === 'done' && (
                  <button
                    onClick={downloadReady}
                    disabled={downloading !== null}
                    className="rounded-lg border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Скачать
                  </button>
                )}
                <button
                  onClick={() => setExportJob(null)}
                  className="rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)]"
                >
                  Скрыть
                </button>
              </div>
            </div>
            
            {/* Прогресс-бар */}
            {(exportJob.status === 'pending' || exportJob.status === 'running') && (
              <div className="space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--app-surface-subtle)]">
                  <div
                    className={`h-full bg-primary-600 transition-all duration-300 ${
                      exportJob.status === 'running' ? 'animate-pulse' : ''
                    }`}
                    style={{
                      width: exportJob.status === 'running' ? '70%' : '10%',
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-[var(--app-text-muted)]">
                  <span>
                    Примерный размер: {getEstimatedSize()}
                  </span>
                  {exportJob.startedAt && (
                    <span>
                      Время: {formatDuration(exportJob.startedAt, exportJob.finishedAt)}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {exportJob.status === 'done' && exportJob.finishedAt && exportJob.startedAt && (
              <div className="text-xs text-[var(--app-text-muted)]">
                Файл сформирован за {formatDuration(exportJob.startedAt, exportJob.finishedAt)}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          title={<span className="text-lg font-semibold text-slate-900">Список покупателей</span>}
          description="Все покупатели магазина"
        />
        <CardBody className="space-y-4">
          {/* Поиск */}
          <div className="flex items-center gap-2 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2 shadow-sm">
            <MagnifyingGlassIcon className="h-5 w-5 text-[var(--app-text-muted)]" />
            <input
              type="search"
              placeholder="Поиск по логину, ID или реквизитам"
              className="w-full border-none bg-transparent text-sm text-[var(--app-text)] outline-none placeholder:text-[var(--app-text-muted)]"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          {/* Таблица покупателей */}
          {isLoading ? (
            <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
              Загружаем покупателей...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-red-600">
              Ошибка загрузки: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
            </div>
          ) : filteredBuyers.length === 0 ? (
            <EmptyState
              title="Покупатели не найдены"
              description="Попробуйте изменить поисковый запрос или обновите список."
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>ID</TableHeaderCell>
                    <TableHeaderCell>Логин</TableHeaderCell>
                    <TableHeaderCell>ID реквизитов</TableHeaderCell>
                    <TableHeaderCell>Дата создания</TableHeaderCell>
                    <TableHeaderCell numeric>Действия</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBuyers.map((buyer) => (
                    <TableRow key={buyer.id}>
                      <TableCell className="font-semibold">#{buyer.id}</TableCell>
                      <TableCell>{buyer.login}</TableCell>
                      <TableCell>{buyer.requisites_id || '—'}</TableCell>
                      <TableCell>
                        {buyer.created_at
                          ? new Date(buyer.created_at).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </TableCell>
                      <TableCell numeric>
                        <button
                          onClick={() => handleViewDetails(buyer)}
                          className="text-sm font-semibold text-primary-600 transition hover:text-primary-700 flex items-center gap-1"
                        >
                          <EyeIcon className="h-4 w-4" />
                          Детали
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Пагинация */}
          {buyersQuery.data?.meta && buyersQuery.data.meta.total > pageSize && (
            <div className="flex items-center justify-between rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-3 text-sm">
              <div>
                Показано {buyersQuery.data.meta.offset + 1}-
                {Math.min(buyersQuery.data.meta.offset + buyersQuery.data.meta.limit, buyersQuery.data.meta.total)} из{' '}
                {buyersQuery.data.meta.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Назад
                </button>
                <span className="text-sm text-[var(--app-text-muted)]">
                  Страница {page} из {Math.ceil(buyersQuery.data.meta.total / pageSize)}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(buyersQuery.data.meta.offset + buyersQuery.data.meta.limit) >= buyersQuery.data.meta.total || isLoading}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Вперёд
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Модальное окно с деталями покупателя */}
      {selectedBuyer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:px-6">
          <div
            className="absolute inset-0 bg-[var(--app-bg)]/70 backdrop-blur-sm transition"
            onClick={() => setSelectedBuyer(null)}
          />
          <div className="relative z-10 w-full max-w-4xl overflow-hidden rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-lifted max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-[var(--app-border)] px-5 py-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">Детали покупателя</p>
                <h2 className="text-base font-semibold text-[var(--app-heading)]">
                  {selectedBuyer.buyer.login} (ID: {selectedBuyer.buyer.id})
                </h2>
              </div>
              <IconButton aria-label="Закрыть" onClick={() => setSelectedBuyer(null)}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5 text-sm">
              {/* Статистика */}
              <Card className="shadow-none border border-[var(--app-border)] bg-[var(--app-surface-subtle)]/70">
                <CardHeader title="Статистика" />
                <CardBody className="space-y-3">
                  {buyerDetailsQuery.isLoading ? (
                    <div className="text-center text-[var(--app-text-muted)]">Загрузка статистики...</div>
                  ) : buyerDetailsQuery.error ? (
                    <div className="text-center text-red-600">
                      Ошибка загрузки статистики: {buyerDetailsQuery.error instanceof Error ? buyerDetailsQuery.error.message : 'Неизвестная ошибка'}
                    </div>
                  ) : (
                    <>
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500 mb-2">Пополнения</p>
                        <div className="space-y-1">
                          {Object.keys(selectedBuyer.statistics.total_deposits).length > 0 ? (
                            Object.entries(selectedBuyer.statistics.total_deposits).map(([currency, amount]) => (
                              <p key={currency} className="text-base font-semibold text-green-600">
                                {formatCurrency(amount, currency)}
                              </p>
                            ))
                          ) : (
                            <p className="text-sm text-[var(--app-text-muted)]">Нет пополнений</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500 mb-2">Выводы</p>
                        <div className="space-y-1">
                          {Object.keys(selectedBuyer.statistics.total_withdrawals).length > 0 ? (
                            Object.entries(selectedBuyer.statistics.total_withdrawals).map(([currency, amount]) => (
                              <p key={currency} className="text-base font-semibold text-red-600">
                                {formatCurrency(amount, currency)}
                              </p>
                            ))
                          ) : (
                            <p className="text-sm text-[var(--app-text-muted)]">Нет выводов</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500 mb-2">Баланс</p>
                        <div className="space-y-1">
                          {Object.keys(selectedBuyer.statistics.net_balance).length > 0 ? (
                            Object.entries(selectedBuyer.statistics.net_balance).map(([currency, amount]) => (
                              <p
                                key={currency}
                                className={`text-base font-semibold ${amount >= 0 ? 'text-green-600' : 'text-red-600'}`}
                              >
                                {formatCurrency(amount, currency)}
                              </p>
                            ))
                          ) : (
                            <p className="text-sm text-[var(--app-text-muted)]">Нет данных</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>

              {/* Отправители */}
              <Card className="shadow-none border border-[var(--app-border)] bg-[var(--app-surface-subtle)]/70">
                <CardHeader title="Отправители" description="Люди, которые отправляли деньги этому покупателю" />
                <CardBody>
                  {buyerDetailsQuery.isLoading ? (
                    <div className="text-center text-[var(--app-text-muted)]">Загрузка отправителей...</div>
                  ) : buyerDetailsQuery.error ? (
                    <div className="text-center text-red-600">
                      Ошибка загрузки отправителей: {buyerDetailsQuery.error instanceof Error ? buyerDetailsQuery.error.message : 'Неизвестная ошибка'}
                    </div>
                  ) : selectedBuyer.senders.length === 0 ? (
                    <EmptyState title="Отправители не найдены" description="Нет информации об отправителях для этого покупателя." />
                  ) : (
                    <div className="space-y-3">
                      {selectedBuyer.senders.map((sender, index) => (
                        <div
                          key={index}
                          className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)]/80 p-4"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-semibold text-[var(--app-heading)]">{sender.sender_name}</p>
                              {sender.sender_account && (
                                <p className="text-xs text-[var(--app-text-muted)]">Счет: {sender.sender_account}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-base font-semibold text-[var(--app-heading)]">
                                {formatCurrency(sender.total_amount, sender.currency)}
                              </p>
                              <p className="text-xs text-[var(--app-text-muted)]">
                                {sender.transaction_count} {sender.transaction_count === 1 ? 'транзакция' : 'транзакций'}
                              </p>
                            </div>
                          </div>
                          <div className="text-xs text-[var(--app-text-muted)] space-y-1">
                            <p>
                              Первая транзакция:{' '}
                              {new Date(sender.first_transaction_at).toLocaleString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                            <p>
                              Последняя транзакция:{' '}
                              {new Date(sender.last_transaction_at).toLocaleString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Buyers;
